import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AppContextService, AppContext } from './app-context.service';
import { RealAiApiService, AiApiRequest, AiApiResponse } from './real-ai-api.service';
import { UserService } from './user.service';

/**
 * Chat message types
 */
export type MessageType = 'user' | 'ai' | 'system' | 'error';

/**
 * Interface for a single chat message
 */
export interface ChatMessage {
  id: string;
  type: MessageType;
  content: string;
  timestamp: Date;
  context?: AppContext;
}

/**
 * Interface for suggested prompts
 */
export interface SuggestedPrompt {
  text: string;
  icon?: string;
  task?: string; // Task identifier sent to backend (e.g., 'careplan', 'vitals', 'medications')
}

/**
 * AI Chat Service
 * Manages chat functionality, message history, and AI interactions
 * Automatically injects context into every AI request
 */
@Injectable({
  providedIn: 'root'
})
export class AiChatService {
  private messagesSubject = new BehaviorSubject<ChatMessage[]>([]);
  public messages$: Observable<ChatMessage[]> = this.messagesSubject.asObservable();

  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$: Observable<boolean> = this.loadingSubject.asObservable();

  private suggestedPromptsSubject = new BehaviorSubject<SuggestedPrompt[]>([]);
  public suggestedPrompts$: Observable<SuggestedPrompt[]> = this.suggestedPromptsSubject.asObservable();

  private currentContext: AppContext;
  private readonly MAX_MESSAGES = 50;
  public responseFronGet: any;

  constructor(
    private appContextService: AppContextService,
    private realAiApiService: RealAiApiService,
    private userService: UserService
  ) {
    console.log('🤖 AI Chat Service initialized with REAL API');

    // Subscribe to context changes
    this.appContextService.context$.subscribe(context => {
      this.currentContext = context;
      this.updateSuggestedPrompts(context);
    });


    // Get initial context
    this.currentContext = this.appContextService.getContext();
    this.updateSuggestedPrompts(this.currentContext);
  }

  /**
   * Send a user message and get AI response
   * Automatically injects current context into the request
   * @param messageText - The user's message
   * @param taskId - Optional task identifier from suggested prompts
   */
  public sendMessage(messageText: string, taskId?: string): void {
    if (!messageText || !messageText.trim()) {
      return;
    }

    // Add user message to chat
    const userMessage: ChatMessage = {
      id: this.generateMessageId(),
      type: 'user',
      content: messageText.trim(),
      timestamp: new Date(),
      context: this.currentContext
    };
    /*
            let a = {
              patient_id: "P0001",
              session_id: "S1",
              chat_id: "C1",
              message: "Hi, I have diabetes. What should I focus on daily?"
            }*/
    this.addMessage(userMessage);

    // Set loading state
    this.loadingSubject.next(true);

    // Build AI API request with context
    const request: AiApiRequest = {
      message: messageText.trim(),
      user_id: this.userService.current_user?.id,
      practice_id: this.userService.current_user?.profileable_id,
      screen: this.currentContext.screen_name,
      active_tab: this.currentContext.active_tab,
      patient_id: this.currentContext.patient_id?.toString(),
      r_patient_id: this.currentContext.patient_id,
      program_id: this.currentContext.program_id,
      timestamp: new Date().toISOString(),
      session_id: 'S1',
      chat_id: 'C1'
    };

    // Include task identifier if provided (from suggested prompts)
    if (taskId) {
      request.task = taskId;
      console.log('📋 Sending message with task identifier:', taskId);
    }




    console.log('AI Request with context:', request);

    // Call Real AI API
    this.realAiApiService.sendMessage(request).subscribe(
      (response) => {
        // Add AI response to chat
        /*                const aiMessage: ChatMessage = {
                            id: this.generateMessageId(),
                            type: 'ai',
                            content: response.message,
                            timestamp: new Date(response.timestamp),
                            context: this.currentContext
                        };*/
        const aiMessage: ChatMessage = {
          id: response['chat_id'],
          type: 'ai',
          content: response['answer'],
          timestamp: new Date(),
          context: this.currentContext
        };
        this.addMessage(aiMessage);
        this.loadingSubject.next(false);
      },
      (error) => {
        // Add error message to chat
        const errorMessage: ChatMessage = {
          id: this.generateMessageId(),
          type: 'error',
          content: 'I apologize, but I\'m having trouble connecting right now. Please try again in a moment.',
          timestamp: new Date()
        };
        this.addMessage(errorMessage);
        this.loadingSubject.next(false);
        console.error('AI API Error:', error);
      }
    );
  }


  getCurrentMessage() {
    this.realAiApiService.getCurrentUserChats().then(resp => {
      const aiMessage: ChatMessage = {
        id: this.generateMessageId(),
        type: 'ai',
        content: resp,
        timestamp: new Date(resp.timestamp),
        context: this.currentContext
      };
      this.responseFronGet = resp

    }).catch(error => {
      const errorMessage: ChatMessage = {
        id: this.generateMessageId(),
        type: 'error',
        content: 'I apologize, but I\'m having trouble connecting right now. Please try again in a moment.',
        timestamp: new Date()
      };
      this.addMessage(errorMessage);
      this.loadingSubject.next(false);

    });
  }

  /**
   * Add a message to the chat history
   * @param message - The message to add
   */
  private addMessage(message: ChatMessage): void {
    const currentMessages = this.messagesSubject.value;
    const updatedMessages = [...currentMessages, message];

    // Limit message history to prevent memory issues
    if (updatedMessages.length > this.MAX_MESSAGES) {
      updatedMessages.shift(); // Remove oldest message
    }

    this.messagesSubject.next(updatedMessages);
  }

  /**
   * Add a system message
   * @param content - Message content
   */
  private addSystemMessage(content: string): void {
    const message: ChatMessage = {
      id: this.generateMessageId(),
      type: 'system',
      content,
      timestamp: new Date()
    };
    this.addMessage(message);
  }

  /**
   * Generate a unique message ID
   */
  private generateMessageId(): string {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Update suggested prompts based on current context
   * @param context - Current app context
   */
  private updateSuggestedPrompts(context: AppContext): void {
    const prompts: SuggestedPrompt[] = [];

    // Context-specific prompts for enrolled patient screens
    if (context.screen_name.includes('enrolled-patient') || context.active_tab.includes('enrolled-patient')) {
      if (context.active_tab.includes('profile')) {
        prompts.push(
          { text: 'Create CCM care plans, include age, conditions, vitals, medications, and allergies for best results', task: 'careplan' },
          { text: 'Show me a Patients current vitals', task: 'vitals' },
          { text: 'List a patients medications and allergies', task: 'medications' }
        );
      } else if (context.active_tab.includes('care-plan')) {
        prompts.push(
          { text: 'Review care plan goals', task: 'careplan_review' },
          { text: 'Show task completion status', task: 'tasks' },
          { text: 'Suggest care plan updates', task: 'careplan_update' }
        );
      } else if (context.active_tab.includes('vitals')) {
        prompts.push(
          { text: 'Analyze vital trends', task: 'vitals_trends' },
          { text: 'Show abnormal readings', task: 'vitals_abnormal' },
          { text: 'Compare to previous month', task: 'vitals_compare' }
        );
      } else {
        prompts.push(
          { text: 'Create Care Plan', task: 'careplan' },
          { text: 'Track and Monitor Vitals', task: 'analytics' },
          { text: 'General Health Information', task: 'chat' },
         /* { text: 'Health Records', task: 'records' }*/
        );
      }
    }
    // Context-specific prompts for program screens
    else if (context.screen_name.includes('program')) {
      prompts.push(
        { text: 'Overview of active programs', task: 'programs_overview' },
        { text: 'Show enrollment trends', task: 'enrollment_trends' },
        { text: 'List pending reviews', task: 'pending_reviews' },
        { text: 'Program performance metrics', task: 'performance_metrics' }
      );
    }
    // General prompts
    else {
      prompts.push(
        { text: 'What can you help me with?', task: 'help' },
        { text: 'Show my tasks for today', task: 'tasks_today' },
        { text: 'Recent patient alerts', task: 'alerts' }
      );
    }

    this.suggestedPromptsSubject.next(prompts);
  }

  /**
   * Get current suggested prompts
   * @returns Array of suggested prompts
   */
  public getSuggestedPrompts(): SuggestedPrompt[] {
    return this.suggestedPromptsSubject.value;
  }

  /**
   * Get current messages
   * @returns Array of chat messages
   */
  public getMessages(): ChatMessage[] {
    return this.messagesSubject.value;
  }

  /**
   * Clear all chat messages
   */
  public clearHistory(): void {
    this.messagesSubject.next([]);
    this.addSystemMessage('How can I help you?');
  }

  /**
   * Check if chat is currently loading
   * @returns Loading state
   */
  public isLoading(): boolean {
    return this.loadingSubject.value;
  }
}
